/**
 * HospitalManagedOrdersPanel
 * --------------------------
 * Hospital-facing read-only view of orders Sterile Mate has placed with
 * suppliers ON BEHALF of this hospital (the admin's `/admin/supplier-orders`
 * board, scoped + sanitized).
 *
 * Hospital ONLY sees:
 *   • PO + their own internal PO #, description, qty
 *   • Hospital price (what we billed them — NEVER the supplier cost / our margin)
 *   • Status (friendly label), expected ship / delivered, tracking + carrier link
 *
 * All supplier/cost/margin/commission fields are stripped server-side by
 * `GET /api/hospital/my-supplier-orders` — this component would not be able
 * to render them even if it tried.
 *
 * Orders without a hospital_price set are excluded by the API (admin must
 * "Sync Dashboards" + set a hospital invoice amount first).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Boxes, RefreshCw, Loader2, Truck, Package, Clock,
  CheckCircle2, AlertCircle, ExternalLink,
} from "lucide-react";
import { API } from "@/App";

const STATUS_CHIP = {
  "Order Received":   { cls: "bg-blue-100 text-blue-800",        icon: AlertCircle },
  "Order Submitted":  { cls: "bg-violet-100 text-violet-800",    icon: Package },
  "Backordered":      { cls: "bg-orange-100 text-orange-800",    icon: Clock },
  "Partial Shipment": { cls: "bg-purple-100 text-purple-800",    icon: Truck },
  "Shipped":          { cls: "bg-cyan-100 text-cyan-800",        icon: Truck },
  "Delivered":        { cls: "bg-emerald-100 text-emerald-800",  icon: CheckCircle2 },
  "Invoiced":         { cls: "bg-indigo-100 text-indigo-800",    icon: CheckCircle2 },
  "Paid":             { cls: "bg-green-100 text-green-800",      icon: CheckCircle2 },
};

const carrierTrackUrl = (carrier, tracking) => {
  if (!tracking) return null;
  const c = (carrier || "").toLowerCase();
  if (c.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tracking)}`;
  if (c.includes("ups"))   return `https://www.ups.com/track?tracknum=${encodeURIComponent(tracking)}`;
  if (c.includes("usps"))  return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(tracking)}`;
  if (c.includes("dhl"))   return `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(tracking)}`;
  return null;
};

const fmt$ = (n) =>
  n === null || n === undefined ? "—" : `$${Number(n).toFixed(2)}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function HospitalManagedOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = useMemo(() => {
    const t = localStorage.getItem("hospital_token") || localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/api/hospital/my-supplier-orders`, { headers });
      setOrders(r.data?.orders || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not load managed orders.";
      setError(msg);
      // Soft-fail — this panel is supplementary; don't toast on auth-missing.
      if (e?.response?.status && e.response.status >= 500) {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    load();
    // Re-fetch when admin clicks "Sync Dashboards" — they re-focus this tab.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const totalBilled = useMemo(
    () => orders.reduce((s, o) => s + (Number(o.hospital_price) || 0), 0),
    [orders],
  );

  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
      data-testid="hospital-managed-orders-panel"
    >
      <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div>
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Boxes className="w-5 h-5 text-blue-600" />
            Orders Placed By Sterile Mate
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            Items Sterile Mate sourced and ordered on your behalf. Tracking,
            expected delivery and final price are pushed to this view as the
            order progresses.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 shrink-0"
          data-testid="hospital-managed-orders-refresh"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 px-5 py-4 bg-slate-50/50 border-b border-slate-200">
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Active Orders</div>
          <div className="text-xl font-extrabold text-slate-900 leading-none mt-1" data-testid="managed-orders-count">{orders.length}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">In Transit</div>
          <div className="text-xl font-extrabold text-cyan-700 leading-none mt-1">
            {orders.filter((o) => o.status === "shipped" || o.status === "partially_shipped").length}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total Billed</div>
          <div className="text-xl font-extrabold text-blue-700 leading-none mt-1">{fmt$(totalBilled)}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              <th className="px-4 py-2 text-left">PO</th>
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-right">Qty</th>
              <th className="px-4 py-2 text-right">Price</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Tracking</th>
              <th className="px-4 py-2 text-left">ETA / Delivered</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-rose-600 text-sm">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && orders.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-10 text-center text-slate-500">
                  <Boxes className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <div className="text-sm font-semibold text-slate-700">No managed orders yet</div>
                  <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                    When Sterile Mate places an order with a supplier on your behalf,
                    it will appear here with tracking and delivery details.
                  </div>
                </td>
              </tr>
            )}
            {!loading && !error && orders.map((o) => {
              const chip = STATUS_CHIP[o.status_label] || { cls: "bg-slate-100 text-slate-700", icon: Clock };
              const Icon = chip.icon;
              const trackUrl = carrierTrackUrl(o.ship_method, o.tracking_number);
              return (
                <tr key={o.id} className="hover:bg-slate-50/50" data-testid={`managed-order-row-${o.po_number || o.id}`}>
                  <td className="px-4 py-3 align-top">
                    <div className="font-mono text-xs font-bold text-slate-800">{o.po_number || "—"}</div>
                    {o.hospital_po && <div className="text-[10px] text-slate-400">Your PO: {o.hospital_po}</div>}
                  </td>
                  <td className="px-4 py-3 align-top max-w-md">
                    <div className="text-slate-800 text-sm" title={o.description}>{o.description || "—"}</div>
                  </td>
                  <td className="px-4 py-3 align-top text-right text-slate-700">{o.qty || "—"}</td>
                  <td className="px-4 py-3 align-top text-right font-bold text-blue-700" data-testid={`managed-order-price-${o.po_number || o.id}`}>
                    {fmt$(o.hospital_price)}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded ${chip.cls}`}>
                      <Icon className="w-3 h-3" />
                      {o.status_label}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {o.tracking_number ? (
                      trackUrl ? (
                        <a
                          href={trackUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1 font-mono text-xs"
                          data-testid={`managed-order-track-link-${o.po_number || o.id}`}
                        >
                          {o.tracking_number}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="font-mono text-xs text-slate-700">{o.tracking_number}</span>
                      )
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                    {o.ship_method && (
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{o.ship_method}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-slate-600">
                    {o.delivered_date ? (
                      <span className="text-emerald-700 font-semibold">Delivered {fmtDate(o.delivered_date)}</span>
                    ) : o.expected_ship_date ? (
                      <span>ETA {fmtDate(o.expected_ship_date)}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
